import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from './util.js';

export interface CheckProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> {
  label: ReactNode;
  error?: string | undefined;
}

/** Checkbox on the native input; the box is drawn around it. */
export const Checkbox = forwardRef<HTMLInputElement, CheckProps>(function Checkbox(
  { label, error, className, id, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <div className={cx('k-choice', error !== undefined && 'k-choice--error', className)}>
      <label htmlFor={inputId} className="k-choice__row">
        <input {...rest} ref={ref} id={inputId} type="checkbox" className="k-choice__box" />
        <span className="k-t-body-lg">{label}</span>
      </label>
      {error === undefined ? null : (
        <p role="alert" className="k-field__note k-t-label-sm">
          {error}
        </p>
      )}
    </div>
  );
});

/** A switch is a checkbox with the switch role, so it toggles with Space like one. */
export const Switch = forwardRef<HTMLInputElement, CheckProps>(function Switch(
  { label, className, id, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <div className={cx('k-choice', className)}>
      <label htmlFor={inputId} className="k-choice__row k-choice__row--switch">
        <span className="k-t-body-lg">{label}</span>
        <input
          {...rest}
          ref={ref}
          id={inputId}
          type="checkbox"
          role="switch"
          className="k-switch"
        />
      </label>
    </div>
  );
});

export interface RadioCardProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> {
  label: ReactNode;
  description?: ReactNode;
}

/** A radio drawn as a card. Group them with the same name inside a `fieldset` with a `legend`. */
export const RadioCard = forwardRef<HTMLInputElement, RadioCardProps>(function RadioCard(
  { label, description, className, id, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <label htmlFor={inputId} className={cx('k-radiocard k-layer', className)}>
      <input {...rest} ref={ref} id={inputId} type="radio" className="k-radiocard__input" />
      <span className="k-radiocard__mark" aria-hidden="true" />
      <span className="k-radiocard__text">
        <span className="k-t-title-sm">{label}</span>
        {description === undefined ? null : <span className="k-t-body k-muted">{description}</span>}
      </span>
    </label>
  );
});
